import { z } from 'zod';
import {
  GAME_IDS,
  LEVEL_IDS,
  MISTAKE_TYPES,
  PUBLICATION_STATUSES,
  SCHOOL_TYPES,
  TEACHING_LANGUAGES,
  VERIFICATION_STATUSES,
} from '@/domain/ids';

/**
 * Curriculum content model.
 *
 * Education system → curriculum version (academic year) → school type / variant
 * → level → subject → domain → learning objective → lesson → exercise items
 * → mastery criteria.
 *
 * Content is data: adding a year, a subject or a revision means adding a pack,
 * not changing application code.
 */

/** Arabic is required; French and English fall back to Arabic when missing. */
export const localizedSchema = z.object({
  ar: z.string().min(1),
  fr: z.string().min(1).optional(),
  en: z.string().min(1).optional(),
});
export type Localized = z.infer<typeof localizedSchema>;

const contentId = z
  .string()
  .regex(/^[a-z0-9]+([.-][a-z0-9]+)*$/, 'ids are lowercase, dot or dash separated');

export const sourceSchema = z.object({
  id: contentId,
  title: z.string().min(1),
  publisher: z.string().min(1),
  url: z.url().optional(),
  accessedAt: z.string().optional(),
  notes: z.string().optional(),
});

export const sourceRefSchema = z.object({
  sourceId: contentId,
  page: z.string().optional(),
  note: z.string().optional(),
});

const governance = {
  status: z.enum(PUBLICATION_STATUSES),
  verification: z.enum(VERIFICATION_STATUSES),
};

export const levelSchema = z.object({
  id: z.enum(LEVEL_IDS),
  order: z.number().int(),
  name: localizedSchema,
  /** Official grouping of primary years (الدرجة الأولى/الثانية/الثالثة). */
  stage: z.enum(['preschool', 'degree1', 'degree2', 'degree3']),
  typicalAge: z.object({ min: z.number().int(), max: z.number().int() }),
});

export const schoolTypeSchema = z.object({
  id: z.enum(SCHOOL_TYPES),
  name: localizedSchema,
});

export const variantSchema = z.object({
  id: contentId,
  name: localizedSchema,
  description: localizedSchema,
  schoolTypes: z.array(z.enum(SCHOOL_TYPES)).min(1),
  ...governance,
});

export const subjectSchema = z.object({
  id: contentId,
  name: localizedSchema,
  /** Short glyph shown in the subject badge (e.g. "ض", "Fr", "+"). */
  glyph: z.string().min(1).max(3),
  color: z.enum(['math', 'arabic', 'french', 'english', 'science']),
  teachingLanguage: z.enum(TEACHING_LANGUAGES),
  levels: z.array(z.enum(LEVEL_IDS)).min(1),
  availabilityNote: localizedSchema.optional(),
  sources: z.array(sourceRefSchema),
  ...governance,
});

export const domainSchema = z.object({
  id: contentId,
  subjectId: contentId,
  name: localizedSchema,
});

export const MATH_FORMATS = [
  'input',
  'choice',
  'missing',
  'findMistake',
  'trueFalse',
  'match',
  'numberLine',
  'count',
  'nextNumber',
  'wordProblem',
] as const;
export type MathFormat = (typeof MATH_FORMATS)[number];

export const ARITHMETIC_OPERATIONS = ['add', 'sub', 'mul', 'div'] as const;
export type ArithmeticOperation = (typeof ARITHMETIC_OPERATIONS)[number];

export const BAND_IDS = ['b1', 'b2', 'b3', 'mixed'] as const;
export type BandId = (typeof BAND_IDS)[number];

export const arithmeticParamsSchema = z.object({
  operations: z.array(z.enum(ARITHMETIC_OPERATIONS)).min(1),
  band: z.enum(BAND_IDS),
  formats: z.array(z.enum(MATH_FORMATS)).min(1),
  /** Multiplication tables to practise (e.g. [2, 3, 4, 5]); defaults to the band. */
  tables: z.array(z.number().int().min(1).max(12)).optional(),
});
export type ArithmeticParams = z.infer<typeof arithmeticParamsSchema>;

export const countingParamsSchema = z.object({
  min: z.number().int().min(0),
  max: z.number().int().min(1).max(20),
  formats: z.array(z.enum(['count', 'numberLine', 'nextNumber'])).min(1),
});
export type CountingParams = z.infer<typeof countingParamsSchema>;

export const practiceSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('arithmetic'), params: arithmeticParamsSchema }),
  z.object({ type: z.literal('counting'), params: countingParamsSchema }),
  /** Authored items from the pack's item bank (items reference the objective). */
  z.object({ type: z.literal('bank') }),
]);
export type Practice = z.infer<typeof practiceSchema>;

export const masteryOverrideSchema = z.object({
  minAttempts: z.number().int().positive().optional(),
  accuracy: z.number().min(0).max(1).optional(),
});

export const objectiveSchema = z.object({
  id: contentId,
  subjectId: contentId,
  domainId: contentId,
  levels: z.array(z.enum(LEVEL_IDS)).min(1),
  /** Restrict to some curriculum variants; omitted = every variant. */
  variants: z.array(contentId).optional(),
  /** Position in the learning map of the subject. */
  order: z.number().int(),
  title: localizedSchema,
  description: localizedSchema,
  difficulty: z.number().int().min(1).max(5),
  teachingLanguage: z.enum(TEACHING_LANGUAGES),
  prerequisites: z.array(contentId),
  practice: practiceSchema,
  games: z.array(z.enum(GAME_IDS)).min(1),
  mastery: masteryOverrideSchema.optional(),
  sources: z.array(sourceRefSchema),
  ...governance,
});

const visualSchema = z.object({
  emoji: z.string().min(1).max(64),
  label: z.string().min(1),
});

const n = () => z.number().int().min(0).max(9999);
const text = () => z.string().min(1).max(80);

/**
 * Illustrations ("figures") shared by lessons and generated exercises. They
 * are data: the app draws them (src/features/illustrations) in the writing
 * direction of the content language.
 */
export const figureSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('emoji'), emoji: z.string().min(1).max(64), label: z.string() }),
  z.object({ type: z.literal('count'), count: n(), emoji: z.string().min(1), label: z.string() }),
  z.object({
    type: z.literal('numberLine'),
    min: n(),
    max: n(),
    marked: n().optional(),
    jump: z.object({ from: n(), by: z.number().int() }).optional(),
  }),
  /** b groups of a items: multiplication as repeated addition. */
  z.object({
    type: z.literal('groups'),
    groups: n(),
    perGroup: n(),
    emoji: z.string().min(1),
    label: z.string(),
  }),
  /** Objects to add (two groups) or to take away (the last ones crossed out). */
  z.object({
    type: z.literal('objects'),
    op: z.enum(['add', 'sub']),
    a: n(),
    b: n(),
    emoji: z.string().min(1),
  }),
  /** Part–whole bar model; null marks the unknown. */
  z.object({
    type: z.literal('bar'),
    total: n().nullable(),
    parts: z.array(n().nullable()).min(2).max(3),
  }),
  /**
   * Column (vertical) operation with carries. With `horizontal`, the same
   * operation is also written on one line next to it («العمليّة الأفقيّة»).
   */
  z.object({
    type: z.literal('column'),
    op: z.enum(['add', 'sub', 'mul']),
    a: n(),
    b: n(),
    showResult: z.boolean(),
    horizontal: z.boolean().optional(),
  }),
  /** Ten ones tied into one ten («قانون العقد»), then the same number in base-ten blocks. */
  z.object({ type: z.literal('bundle'), value: z.number().int().min(1).max(39) }),
  /** Base-ten blocks: hundreds, tens and ones. */
  z.object({ type: z.literal('baseTen'), value: n() }),
  /** A row of picture cards (senses, seasons, vowel marks…). */
  z.object({
    type: z.literal('cards'),
    cards: z
      .array(z.object({ emoji: z.string().min(1).max(16), title: text(), text: text().optional() }))
      .min(2)
      .max(6),
  }),
  /** Steps of a transformation with labelled arrows (states of water…). */
  z.object({
    type: z.literal('sequence'),
    steps: z
      .array(z.object({ emoji: z.string().min(1).max(16), label: text() }))
      .min(2)
      .max(4),
    arrows: z.array(text()),
  }),
  /** Labelled diagram of a flowering plant. */
  z.object({
    type: z.literal('plantParts'),
    labels: z.object({ flower: text(), leaves: text(), stem: text(), roots: text() }),
  }),
]);
export type Figure = z.infer<typeof figureSchema>;

export const lessonStepSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('intro'), text: z.string().min(1) }),
  z.object({
    type: z.literal('illustration'),
    figure: figureSchema,
    caption: z.string().optional(),
  }),
  z.object({
    type: z.literal('example'),
    text: z.string().min(1),
    /** Written left-to-right (mathematics) or in the teaching language. */
    expression: z.string().optional(),
    visual: visualSchema.optional(),
  }),
  z.object({ type: z.literal('tip'), text: z.string().min(1) }),
  /** A rule to remember, highlighted, with a picture and several examples. */
  z.object({
    type: z.literal('rule'),
    title: z.string().min(1).max(60),
    text: z.string().min(1),
    figure: figureSchema.optional(),
    caption: z.string().optional(),
    examples: z
      .array(
        z.object({
          /** Written like `expression` in an example step. */
          expression: z.string().optional(),
          text: z.string().optional(),
        }),
      )
      .max(6)
      .optional(),
  }),
]);

export const lessonSchema = z.object({
  id: contentId,
  objectiveId: contentId,
  title: z.string().min(1),
  steps: z.array(lessonStepSchema).min(1),
  ...governance,
});

const itemBase = {
  id: contentId,
  objectiveId: contentId,
  difficulty: z.number().int().min(1).max(5),
  hint: z.string().optional(),
  explanation: z.string().optional(),
  /** Text to pronounce; a recorded file can be added later via `src`. */
  audio: z.object({ text: z.string().min(1), src: z.string().optional() }).optional(),
  /** Short reading passage shown above the question. */
  passage: z.string().optional(),
  visual: visualSchema.optional(),
  ...governance,
};

const optionSchema = z.object({
  id: z.string().min(1),
  text: z.string().min(1),
  emoji: z.string().optional(),
  /** What choosing this distractor usually reveals. */
  mistake: z.enum(MISTAKE_TYPES).optional(),
});

export const itemSchema = z.discriminatedUnion('kind', [
  z.object({
    ...itemBase,
    kind: z.literal('choice'),
    prompt: z.string().min(1),
    options: z.array(optionSchema).min(2).max(6),
    answer: z.string().min(1),
  }),
  z.object({
    ...itemBase,
    kind: z.literal('trueFalse'),
    statement: z.string().min(1),
    answer: z.boolean(),
    mistake: z.enum(MISTAKE_TYPES).optional(),
  }),
  z.object({
    ...itemBase,
    kind: z.literal('order'),
    prompt: z.string().min(1),
    unit: z.enum(['letter', 'word']),
    /** Tokens in the correct order. */
    tokens: z.array(z.string().min(1)).min(2).max(10),
    /** Other correct orders (e.g. Arabic nominal and verbal sentences). */
    alternatives: z.array(z.array(z.string().min(1))).optional(),
    mistake: z.enum(MISTAKE_TYPES).optional(),
  }),
  z.object({
    ...itemBase,
    kind: z.literal('input'),
    prompt: z.string().min(1),
    answer: z.string().min(1),
    accept: z.array(z.string().min(1)).optional(),
    mistake: z.enum(MISTAKE_TYPES).optional(),
  }),
  z.object({
    ...itemBase,
    kind: z.literal('match'),
    prompt: z.string().min(1),
    pairs: z
      .array(
        z.object({
          left: z.string().min(1),
          right: z.string().min(1),
          emoji: z.string().optional(),
        }),
      )
      .min(2)
      .max(8),
    mistake: z.enum(MISTAKE_TYPES).optional(),
  }),
  z.object({
    ...itemBase,
    kind: z.literal('categorize'),
    prompt: z.string().min(1),
    categories: z
      .array(z.object({ id: z.string().min(1), label: z.string().min(1) }))
      .min(2)
      .max(4),
    entries: z
      .array(
        z.object({
          text: z.string().min(1),
          emoji: z.string().optional(),
          category: z.string().min(1),
        }),
      )
      .min(3)
      .max(10),
    mistake: z.enum(MISTAKE_TYPES).optional(),
  }),
]);
export type ContentItem = z.infer<typeof itemSchema>;

export const curriculumPackSchema = z.object({
  /** Format of this file, bumped on breaking changes to the content model. */
  schemaVersion: z.literal(1),
  id: contentId,
  educationSystem: z.object({ id: z.literal('tn'), name: localizedSchema }),
  curriculumVersion: z.object({
    id: contentId,
    academicYear: z.string().regex(/^\d{4}-\d{4}$/),
    label: localizedSchema,
    ...governance,
    notes: z.string(),
  }),
  sources: z.array(sourceSchema),
  levels: z.array(levelSchema).min(1),
  schoolTypes: z.array(schoolTypeSchema).min(1),
  variants: z.array(variantSchema).min(1),
  subjects: z.array(subjectSchema).min(1),
  domains: z.array(domainSchema),
  objectives: z.array(objectiveSchema),
  lessons: z.array(lessonSchema),
  items: z.array(itemSchema),
});

export type CurriculumPack = z.infer<typeof curriculumPackSchema>;
export type SchoolLevel = z.infer<typeof levelSchema>;
export type SchoolVariant = z.infer<typeof variantSchema>;
export type Subject = z.infer<typeof subjectSchema>;
export type LearningDomain = z.infer<typeof domainSchema>;
export type LearningObjective = z.infer<typeof objectiveSchema>;
export type Lesson = z.infer<typeof lessonSchema>;
export type LessonStep = z.infer<typeof lessonStepSchema>;
export type ContentSource = z.infer<typeof sourceSchema>;
